import { Link } from 'react-router-dom';

export default function PrivacyPage() {
  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="legal-hero">
        <div className="container">
          <div style={{ maxWidth: 680 }}>
            <div className="section-label">Legal</div>
            <h1 className="text-headline" style={{ marginBottom: 12 }}>Privacy Policy</h1>
            <p style={{ fontSize: 'var(--text-base)', color: 'var(--text-muted)' }}>
              Effective date: <strong style={{ color: 'var(--text-secondary)' }}>June 2026</strong>
            </p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 8 }}>
              Last updated: June 2026 · Applies to: Klar mobile application (iOS &amp; Android)
            </p>
          </div>
        </div>
      </section>

      <div className="legal-content">

        {/* TL;DR */}
        <div className="legal-highlight">
          <p>
            <strong>Short version:</strong> Klar stores your budget and expense data locally on your device only. We don't read your bank account, scrape your SMS, or sell your data to anyone. Google Sign-In gives us your name, email, and profile picture — nothing else. Your financial data never leaves your phone.
          </p>
        </div>

        {/* Table of contents */}
        <div className="legal-toc">
          <div className="legal-toc-title">Contents</div>
          <ol className="legal-toc-list" style={{ listStyle: 'decimal', paddingLeft: 20, gap: 8 }}>
            {[
              'Who We Are',
              'What Data We Collect',
              'How We Use Your Data',
              'What We Do NOT Do',
              'Data Storage',
              'Data Deletion',
              'Third-Party Services',
              'Children\'s Privacy',
              'Changes to This Policy',
              'Contact',
            ].map((item, i) => (
              <li key={item} style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', paddingLeft: 4 }}>{item}</li>
            ))}
          </ol>
        </div>

        {/* Sections */}
        <div className="legal-section" id="who-we-are">
          <h2 className="legal-section-title">1. Who We Are</h2>
          <div className="legal-body">
            <p>
              Klar is a personal finance tracking application developed and operated by <strong>Equios</strong>, a student-founded technology project based in India. Klar is publicly available and free on the Apple App Store and Google Play. The primary contact is Affan Ahmed (<a href="mailto:affan@equios.co">affan@equios.co</a>).
            </p>
            <p>
              References to "Klar", "we", "us", or "our" in this document refer to Equios and the Klar application.
            </p>
          </div>
        </div>

        <div className="legal-section" id="what-we-collect">
          <h2 className="legal-section-title">2. What Data We Collect</h2>
          <div className="legal-body">
            <p>Klar collects only the data necessary to provide the budgeting experience. Here is a complete list:</p>

            <p><strong>a) Google Account Information (via Google Sign-In)</strong></p>
            <ul>
              <li>Your display name (e.g., "Priya Raghavan")</li>
              <li>Your Google account email address</li>
              <li>Your Google profile picture URL</li>
              <li>A unique Google user ID</li>
            </ul>
            <p>This information is used only to personalise your in-app greeting and identify your account. We do not access your Google Drive, Gmail, Google Pay, or any other Google service.</p>

            <p><strong>b) Onboarding Information (entered by you)</strong></p>
            <ul>
              <li>Your preferred name (may differ from Google name)</li>
              <li>Your mobile phone number</li>
              <li>Your monthly budget amount</li>
              <li>Your category-level budget allocations (8 categories)</li>
            </ul>

            <p><strong>c) Expense Logs (entered by you)</strong></p>
            <ul>
              <li>Amount of each expense</li>
              <li>Category assigned to the expense</li>
              <li>Timestamp of the expense entry</li>
              <li>Optional note (if you choose to add one)</li>
            </ul>

            <p><strong>d) App Usage Data</strong></p>
            <ul>
              <li>Daily login streak count</li>
              <li>Last active date</li>
            </ul>
          </div>
        </div>

        <div className="legal-section" id="how-we-use">
          <h2 className="legal-section-title">3. How We Use Your Data</h2>
          <div className="legal-body">
            <p>We use the data you provide exclusively to deliver the Klar experience:</p>
            <ul>
              <li>Displaying your name and profile picture in the app interface</li>
              <li>Calculating your remaining budget per category in real time</li>
              <li>Generating the home screen donut chart and category breakdown</li>
              <li>Rendering the Insights weekly bar chart and spending summaries</li>
              <li>Tracking and displaying your daily usage streak</li>
              <li>Persisting your settings and expense history between sessions</li>
            </ul>
            <p>We do not use your data for advertising, profiling, or any purpose beyond operating the app.</p>
          </div>
        </div>

        <div className="legal-section" id="what-we-dont-do">
          <h2 className="legal-section-title">4. What We Do NOT Do</h2>
          <div className="legal-body">
            <p>We believe in being explicit about what we don't do — these are firm commitments, not aspirational statements:</p>

            <div className="legal-no-list">
              {[
                'We do not access your bank account, UPI account, or any payment service.',
                'We do not read, scrape, or process your SMS messages.',
                'We do not share, sell, rent, or trade your data with any third party.',
                'We do not run advertisements of any kind inside the app.',
                'We do not track your location.',
                'We do not sync your financial data to any cloud server.',
                'We do not build a profile of your spending behaviour to sell or monetise.',
                'We do not contact your phone number for any purpose other than direct account support, if you explicitly request it.',
              ].map(item => (
                <div key={item} className="legal-no-item">
                  <div className="legal-no-icon">✕</div>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="legal-section" id="data-storage">
          <h2 className="legal-section-title">5. Data Storage</h2>
          <div className="legal-body">
            <p>
              All data you enter into Klar — your budget, your income and expense logs, your savings goals, your streak, and your settings — is stored exclusively on your device's local storage. You can optionally lock the app behind Face ID, a fingerprint, or a PIN, and export your own data to a CSV file whenever you choose.
            </p>
            <p>
              <strong>No data is transmitted to or stored on any external server.</strong> There is no Klar cloud backend. There is no database holding your financial records remotely. If you uninstall the app, all data is erased from your device automatically.
            </p>
            <p>
              Your Google account authentication token is handled by Google's own secure libraries and is not stored by Klar beyond the current session's requirements.
            </p>
          </div>
        </div>

        <div className="legal-section" id="data-deletion">
          <h2 className="legal-section-title">6. Data Deletion</h2>
          <div className="legal-body">
            <p>To delete all your Klar data:</p>
            <ul>
              <li><strong>Uninstall the app.</strong> All locally stored data (budget, income, expenses, goals, settings) is automatically erased when you uninstall Klar from your device.</li>
              <li><strong>Clear app data.</strong> On Android, go to Settings → Apps → Klar → Storage → Clear Data. On iPhone or iPad, offload or delete the app from Settings → General → iPhone Storage. This wipes all local data.</li>
            </ul>
            <p>
              If you wish to request deletion of any Google Sign-In association or any data we may hold about you (e.g., from support contact), email us at <a href="mailto:affan@equios.co">affan@equios.co</a> and we will action it within 7 business days.
            </p>
          </div>
        </div>

        <div className="legal-section" id="third-parties">
          <h2 className="legal-section-title">7. Third-Party Services</h2>
          <div className="legal-body">
            <p>Klar uses one third-party service:</p>
            <ul>
              <li>
                <strong>Google Sign-In (Google OAuth 2.0)</strong> — Used for authentication. When you sign in with Google, Google's own privacy policy governs how they handle your authentication data. We receive only the account information listed in Section 2(a). Google's privacy policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">policies.google.com/privacy</a>
              </li>
            </ul>
            <p>We do not use Firebase, Crashlytics, Google Analytics, Facebook SDK, or any other analytics or advertising SDK inside Klar.</p>
          </div>
        </div>

        <div className="legal-section" id="childrens-privacy">
          <h2 className="legal-section-title">8. Children's Privacy</h2>
          <div className="legal-body">
            <p>
              Klar is intended for users aged 16 and above (college students). We do not knowingly collect data from children under 16. If you believe a user under 16 has provided us with personal information, please contact us at <a href="mailto:affan@equios.co">affan@equios.co</a>.
            </p>
          </div>
        </div>

        <div className="legal-section" id="changes">
          <h2 className="legal-section-title">9. Changes to This Policy</h2>
          <div className="legal-body">
            <p>
              We may update this Privacy Policy if Klar's data practices change (for example, if we add a cloud sync feature in future). We will notify users in-app or via email before any material changes take effect. The "Effective date" at the top of this page will always reflect the current version.
            </p>
            <p>
              Continued use of Klar after a policy update constitutes acceptance of the updated policy.
            </p>
          </div>
        </div>

        <div className="legal-section" id="contact">
          <h2 className="legal-section-title">10. Contact</h2>
          <div className="legal-body">
            <p>
              For any privacy-related questions, data deletion requests, or concerns:
            </p>
            <ul>
              <li><strong>Email:</strong> <a href="mailto:affan@equios.co">affan@equios.co</a></li>
              <li><strong>Subject line:</strong> "Klar Privacy — [your request]"</li>
            </ul>
            <p>We aim to respond within 3–5 business days.</p>
          </div>
        </div>

        {/* Navigation links */}
        <div style={{ display: 'flex', gap: 16, paddingTop: 32, borderTop: '1px solid var(--border)' }}>
          <Link to="/terms" className="btn btn-ghost btn-sm">Terms &amp; Conditions →</Link>
          <Link to="/contact" className="btn btn-ghost btn-sm">Contact Us →</Link>
        </div>
      </div>
    </>
  );
}
